import { useMemo } from 'react';
import { DebouncedFunc } from 'lodash';

import {
  Box,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  TableContainer,
  InputAdornment,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { AccountProfile } from 'src/services/account/account.types';
import { PageOptions, PaginatedResponse } from 'src/services/api.types';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import {
  useTable,
  emptyRows,
  TableNoData,
  TableEmptyRows,
  TableHeadCustom,
  TablePaginationCustom,
} from 'src/components/table';

type Props = {
  accounts?: PaginatedResponse<AccountProfile>;
  currentPage: PageOptions;
  onChangePage: (_event: any, pageNumber: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFilter?: DebouncedFunc<(value: string) => void>;
};

export default function GlobalAccountsTable({
  accounts,
  onChangePage,
  onChangeRowsPerPage,
  onFilter,
  currentPage,
}: Props) {
  const table = useTable();

  const denseHeight = table.dense ? 56 : 56 + 20;

  const tableLabels = useMemo(
    () => [
      { id: 'id', label: 'ID' },
      { id: 'full_name', label: 'Full Name' },
      { id: 'org_role', label: 'Org Role' },
      { id: 'status', label: 'Account Status' },
      { id: 'phone', label: 'Phone' },
      { id: 'timezone', label: 'Timezone' },
    ],
    []
  );

  const notFound = !accounts?.count;

  return (
    <Box>
      {onFilter && (
        <Stack
          sx={{
            p: 2.5,
            pr: { xs: 2.5, md: 1 },
          }}
        >
          <TextField
            onChange={(e) => onFilter(e.target.value)}
            label="Search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      )}

      <TableContainer sx={{ overflow: 'unset' }}>
        <Scrollbar>
          <Table sx={{ minWidth: 680 }} size={table.dense ? 'small' : 'medium'}>
            <TableHeadCustom headLabel={tableLabels} />

            <TableBody>
              {accounts?.results?.map((row) => <AccountsTableRow key={row.id} row={row} />)}

              <TableEmptyRows
                height={denseHeight}
                emptyRows={emptyRows(table.page, table.rowsPerPage, accounts?.count ?? 0)}
              />

              <TableNoData notFound={notFound} />
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>

      <TablePaginationCustom
        count={accounts?.count ?? 0}
        page={currentPage.page}
        rowsPerPage={currentPage.per_page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        dense={table.dense}
        onChangeDense={table.onChangeDense}
      />
    </Box>
  );
}

const AccountsTableRow = ({ row }: { row: AccountProfile }) => {
  const { t } = useTranslate();

  return (
    <TableRow>
      <TableCell>{row.id}</TableCell>
      <TableCell>{`${row.first_name} ${row.last_name}`}</TableCell>
      <TableCell>{t(`enums.org_role.${row.org_role}`)}</TableCell>
      <TableCell>{t(`enums.account_status.${row.status}`)}</TableCell>
      <TableCell>{row.phone}</TableCell>
      <TableCell>{row.timezone}</TableCell>
    </TableRow>
  );
};
